import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  Plus,
  Pencil,
  Trash2,
  FileText,
  ArrowLeft,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/auth/hooks/useAuth';
import { queryKeys } from '@/lib/query-keys';
import { api } from '@/lib/api';
import type { LearnAboutFiasDocumentResponse, LearnAboutFiasStatsResponse } from '@/lib/api';

// =============================================================================
// Stats Cards
// =============================================================================

function StatsCards({
  stats,
  isLoading,
}: {
  stats: LearnAboutFiasStatsResponse | undefined;
  isLoading: boolean;
}) {
  const items = [
    { label: 'Documents', value: stats?.documentCount ?? 0 },
    { label: 'Chunks', value: stats?.chunkCount ?? 0 },
    { label: 'Pending', value: stats?.pendingDocuments ?? 0 },
    { label: 'Failed', value: stats?.failedDocuments ?? 0 },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label}>
          <CardContent className="p-4 text-center">
            {isLoading ? (
              <Skeleton className="h-8 w-16 mx-auto mb-1" />
            ) : (
              <p className="text-2xl font-bold">{item.value}</p>
            )}
            <p className="text-sm text-muted-foreground">{item.label}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === 'completed'
      ? 'default'
      : status === 'processing'
        ? 'secondary'
        : status === 'failed'
          ? 'destructive'
          : 'outline';

  return <Badge variant={variant}>{status}</Badge>;
}

// =============================================================================
// Document Form Dialog
// =============================================================================

const ACCEPTED_FILE_TYPES = '.pdf,.md,.markdown,.txt,.html,.htm';

interface FormSubmitData {
  title: string;
  description: string;
  sourceUrl: string;
  showInResources: boolean;
  file: { base64: string; name: string } | null;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URL prefix (e.g. "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  initialData,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: FormSubmitData) => void;
  isSubmitting: boolean;
  initialData?: {
    title: string;
    description: string;
    sourceUrl: string;
    showInResources: boolean;
  };
}) {
  const isEdit = !!initialData;
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [sourceUrl, setSourceUrl] = useState(initialData?.sourceUrl ?? '');
  const [showInResources, setShowInResources] = useState(initialData?.showInResources ?? false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = useCallback(
    (file: File) => {
      setSelectedFile(file);
      // Auto-fill title from file name if empty
      if (!title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
        setTitle(nameWithoutExt);
      }
    },
    [title],
  );

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let file: { base64: string; name: string } | null = null;
    if (selectedFile) {
      const base64 = await readFileAsBase64(selectedFile);
      file = { base64, name: selectedFile.name };
    }
    onSubmit({ title, description, sourceUrl, showInResources, file });
  }

  const canSubmit = title.trim() && (selectedFile || isEdit);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Document' : 'Add Document'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File upload area */}
          <div className="space-y-2">
            <Label>Document File</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : selectedFile
                    ? 'border-primary/50 bg-muted/30'
                    : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('doc-file-input')?.click()}
            >
              <input
                id="doc-file-input"
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleInputChange}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-primary shrink-0" />
                  <div className="text-left">
                    <p className="font-medium text-sm">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)} &middot; Click or drop to replace
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drop a file here or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground">PDF, Markdown, Text, or HTML</p>
                </div>
              )}
            </div>
            {isEdit && !selectedFile && (
              <p className="text-xs text-muted-foreground">
                Leave empty to keep existing content. Upload a new file to replace it.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-title">Title</Label>
            <Input
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-description">Description</Label>
            <Input
              id="doc-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description for the resource card"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-source-url">Display URL (optional)</Label>
            <Input
              id="doc-source-url"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://... (link shown on resource card)"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="doc-show-resources"
              checked={showInResources}
              onCheckedChange={setShowInResources}
            />
            <Label htmlFor="doc-show-resources">Show on Resources page</Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !canSubmit}>
              {isSubmitting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {isEdit ? 'Saving...' : 'Creating...'}
                </>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Create Document'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Document Row
// =============================================================================

function DocumentRow({
  doc,
  onEdit,
  onDelete,
  onToggleVisibility,
}: {
  doc: LearnAboutFiasDocumentResponse;
  onEdit: (doc: LearnAboutFiasDocumentResponse) => void;
  onDelete: (docId: string) => void;
  onToggleVisibility: (docId: string, showInResources: boolean) => void;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <FileText className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium truncate">{doc.title}</h3>
                <StatusBadge status={doc.status} />
                <Badge variant="outline">{doc.sourceType}</Badge>
                {doc.totalChunks > 0 && (
                  <span className="text-xs text-muted-foreground">{doc.totalChunks} chunks</span>
                )}
              </div>
              {doc.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
              )}
              {doc.errorMessage && (
                <p className="text-sm text-destructive mt-1">{doc.errorMessage}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Created {new Date(doc.createdAt).toLocaleDateString()}
                {doc.sourceUrl && (
                  <>
                    {' '}
                    &middot;{' '}
                    <a
                      href={doc.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      View source
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2" title="Show on Resources page">
              <Switch
                checked={doc.showInResources}
                onCheckedChange={(checked) => onToggleVisibility(doc.docId, checked)}
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">RAG only</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => onEdit(doc)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(doc.docId)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export function AdminDocumentsPage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const queryClient = useQueryClient();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<LearnAboutFiasDocumentResponse | null>(null);

  // Queries
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.learnAboutFias.adminStats(),
    queryFn: () => api.adminGetLearnAboutFiasStats(),
    enabled: isAdmin,
  });

  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: queryKeys.learnAboutFias.adminDocuments(),
    queryFn: () => api.adminListLearnAboutFiasDocuments({ limit: 100 }),
    enabled: isAdmin,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof api.adminCreateLearnAboutFiasDocument>[0]) =>
      api.adminCreateLearnAboutFiasDocument(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.admin() });
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.resources() });
      setAddDialogOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      docId,
      input,
    }: {
      docId: string;
      input: Parameters<typeof api.adminUpdateLearnAboutFiasDocument>[1];
    }) => api.adminUpdateLearnAboutFiasDocument(docId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.admin() });
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.resources() });
    },
  });

  const updateContentMutation = useMutation({
    mutationFn: ({
      docId,
      input,
    }: {
      docId: string;
      input: Parameters<typeof api.adminUpdateLearnAboutFiasDocumentContent>[1];
    }) => api.adminUpdateLearnAboutFiasDocumentContent(docId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.admin() });
      setEditingDoc(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => api.adminDeleteLearnAboutFiasDocument(docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.admin() });
      queryClient.invalidateQueries({ queryKey: queryKeys.learnAboutFias.resources() });
    },
  });

  // Handlers
  function handleCreate(data: FormSubmitData) {
    if (!data.file) return;
    createMutation.mutate({
      mode: 'file',
      title: data.title,
      description: data.description || undefined,
      fileBase64: data.file.base64,
      fileName: data.file.name,
      sourceUrl: data.sourceUrl || undefined,
      showInResources: data.showInResources,
    });
  }

  function handleEdit(doc: LearnAboutFiasDocumentResponse) {
    setEditingDoc(doc);
  }

  function handleEditSubmit(data: FormSubmitData) {
    if (!editingDoc) return;

    // Update metadata
    updateMutation.mutate({
      docId: editingDoc.docId,
      input: {
        title: data.title,
        description: data.description || null,
        sourceUrl: data.sourceUrl || null,
        showInResources: data.showInResources,
      },
    });

    // If a new file was uploaded, re-ingest with new content
    if (data.file) {
      updateContentMutation.mutate({
        docId: editingDoc.docId,
        input: {
          mode: 'file',
          fileBase64: data.file.base64,
          fileName: data.file.name,
        },
      });
    } else {
      setEditingDoc(null);
    }
  }

  function handleDelete(docId: string) {
    if (
      window.confirm(
        'Are you sure you want to delete this document? This will also remove all its embeddings.',
      )
    ) {
      deleteMutation.mutate(docId);
    }
  }

  function handleToggleVisibility(docId: string, showInResources: boolean) {
    updateMutation.mutate({ docId, input: { showInResources } });
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <ShieldAlert className="h-5 w-5" />
          <p>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const documents = docsData?.documents ?? [];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/a/arche_learn_about_fias">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">Manage Assets</h1>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Document
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Manage documents for the Learn About Fias knowledge base. Documents power both the Resources
        page and the AI Chat RAG system. Toggle visibility to control which documents appear on the
        Resources page.
      </p>

      <StatsCards stats={statsData} isLoading={statsLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {docsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : documents.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No documents yet. Add your first document to get started.
            </p>
          ) : (
            documents.map((doc) => (
              <DocumentRow
                key={doc.docId}
                doc={doc}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleVisibility={handleToggleVisibility}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Add Document Dialog */}
      <DocumentFormDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSubmit={handleCreate}
        isSubmitting={createMutation.isPending}
      />

      {/* Edit Document Dialog */}
      {editingDoc && (
        <DocumentFormDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setEditingDoc(null);
            }
          }}
          onSubmit={handleEditSubmit}
          isSubmitting={updateMutation.isPending || updateContentMutation.isPending}
          initialData={{
            title: editingDoc.title,
            description: editingDoc.description ?? '',
            sourceUrl: editingDoc.sourceUrl ?? '',
            showInResources: editingDoc.showInResources,
          }}
        />
      )}
    </div>
  );
}
